/**
 * AdminStripeConnect — Página para tenant configurar Stripe Connect
 * Onboarding, status, saldo e link pro dashboard
 */

import { h } from 'preact';
import { useState, useEffect } from 'preact/hooks';
import { api } from '../api/client';

interface ConnectStatus {
  connected: boolean;
  accountId?: string;
  charges_enabled: boolean;
  details_submitted: boolean;
  payouts_enabled: boolean;
  onboarding_complete: boolean;
  platform_fee_percent: number;
}

interface BalanceData {
  available: Array<{ amount: number; currency: string }>;
  pending: Array<{ amount: number; currency: string }>;
}

export function AdminStripeConnect() {
  const [status, setStatus] = useState<ConnectStatus | null>(null);
  const [balance, setBalance] = useState<BalanceData | null>(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState('');

  // Buscar status ao montar
  useEffect(() => {
    loadStatus();
  }, []);

  async function loadStatus() {
    try {
      setLoading(true);
      const res = await api.get('/admin/stripe/connect/status');
      const data = res.data || res;
      setStatus(data);

      // Se conectado, buscar saldo
      if (data.connected && data.charges_enabled) {
        try {
          const balRes = await api.get('/admin/stripe/connect/balance');
          const balData = balRes.data || balRes;
          if (balData.ok) setBalance(balData.balance);
        } catch {
          // Saldo pode falhar se conta ainda não processou pagamentos
        }
      }
    } catch (err: any) {
      setError(err.message || 'Erro ao carregar status');
    } finally {
      setLoading(false);
    }
  }

  // Iniciar onboarding
  async function startConnect() {
    try {
      setActionLoading(true);
      setError('');
      const res = await api.post('/admin/stripe/connect', {});
      const data = res.data || res;
      if (data.onboardingUrl) {
        window.open(data.onboardingUrl, '_blank');
      }
      await loadStatus();
    } catch (err: any) {
      setError(err.message || 'Erro ao iniciar conexão');
    } finally {
      setActionLoading(false);
    }
  }

  // Refresh link de onboarding
  async function refreshOnboarding() {
    try {
      setActionLoading(true);
      setError('');
      const res = await api.post('/admin/stripe/connect/refresh', {});
      const data = res.data || res;
      if (data.onboardingUrl) {
        window.open(data.onboardingUrl, '_blank');
      }
    } catch (err: any) {
      setError(err.message || 'Erro ao gerar novo link');
    } finally {
      setActionLoading(false);
    }
  }

  // Abrir dashboard Stripe
  async function openDashboard() {
    try {
      setActionLoading(true);
      const res = await api.get('/admin/stripe/connect/dashboard');
      const data = res.data || res;
      if (data.url) {
        window.open(data.url, '_blank');
      }
    } catch (err: any) {
      setError(err.message || 'Erro ao abrir dashboard');
    } finally {
      setActionLoading(false);
    }
  }

  // Formatar valor em centavos para moeda
  function formatMoney(amount: number, currency: string) {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: currency.toUpperCase(),
    }).format(amount / 100);
  }

  if (loading) {
    return (
      <div style={{ padding: 32, textAlign: 'center', color: '#aaa' }}>
        Carregando configurações do Stripe Connect...
      </div>
    );
  }

  const cardStyle: any = {
    background: 'var(--bg-card, #1a1a2e)',
    borderRadius: 12,
    padding: 24,
    marginBottom: 16,
    border: '1px solid var(--border, #2a2a4a)',
  };

  const btnPrimary: any = {
    background: 'var(--primary, #6C63FF)',
    color: '#fff',
    border: 'none',
    borderRadius: 8,
    padding: '12px 24px',
    fontSize: 15,
    fontWeight: 600,
    cursor: 'pointer',
    opacity: actionLoading ? 0.6 : 1,
  };

  const btnSecondary: any = {
    ...btnPrimary,
    background: 'transparent',
    border: '1px solid var(--primary, #6C63FF)',
    color: 'var(--primary, #6C63FF)',
  };

  const statusDot = (ok: boolean) => (
    <span style={{
      display: 'inline-block',
      width: 10,
      height: 10,
      borderRadius: '50%',
      background: ok ? '#22c55e' : '#f59e0b',
      marginRight: 8,
    }} />
  );

  return (
    <div style={{ padding: '24px 32px', maxWidth: 800 }}>
      <h1 style={{ fontSize: 24, fontWeight: 700, marginBottom: 8, color: 'var(--text, #fff)' }}>
        💳 Stripe Connect
      </h1>
      <p style={{ color: 'var(--text-secondary, #aaa)', marginBottom: 24, fontSize: 14 }}>
        Configure sua conta Stripe para receber pagamentos dos seus clientes com split automático.
      </p>

      {error && (
        <div style={{ background: '#3b1111', border: '1px solid #ef4444', borderRadius: 8, padding: 12, marginBottom: 16, color: '#fca5a5', fontSize: 14 }}>
          {error}
        </div>
      )}

      {/* Status da Conexão */}
      <div style={cardStyle}>
        <h3 style={{ fontSize: 16, fontWeight: 600, marginBottom: 16, color: 'var(--text, #fff)' }}>
          Status da Conexão
        </h3>

        {!status?.connected ? (
          <div>
            <p style={{ color: 'var(--text-secondary, #aaa)', marginBottom: 16, fontSize: 14 }}>
              Sua conta Stripe ainda não está conectada. Conecte para começar a receber pagamentos dos seus clientes.
            </p>
            <button style={btnPrimary} onClick={startConnect} disabled={actionLoading}>
              {actionLoading ? 'Conectando...' : '🔗 Conectar Stripe'}
            </button>
          </div>
        ) : (
          <div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 16 }}>
              <div style={{ fontSize: 14, color: 'var(--text, #fff)' }}>
                {statusDot(status.charges_enabled)}
                Cobranças: {status.charges_enabled ? 'Ativadas ✅' : 'Pendente ⏳'}
              </div>
              <div style={{ fontSize: 14, color: 'var(--text, #fff)' }}>
                {statusDot(status.payouts_enabled)}
                Saques: {status.payouts_enabled ? 'Ativados ✅' : 'Pendente ⏳'}
              </div>
              <div style={{ fontSize: 14, color: 'var(--text, #fff)' }}>
                {statusDot(status.details_submitted)}
                Dados: {status.details_submitted ? 'Completos ✅' : 'Incompletos ⏳'}
              </div>
              <div style={{ fontSize: 14, color: 'var(--text, #fff)' }}>
                {statusDot(status.onboarding_complete)}
                Onboarding: {status.onboarding_complete ? 'Completo ✅' : 'Pendente ⏳'}
              </div>
            </div>

            <div style={{ fontSize: 13, color: 'var(--text-secondary, #aaa)', marginBottom: 16 }}>
              Taxa da plataforma: {status.platform_fee_percent}% por transação
            </div>

            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
              {!status.onboarding_complete && (
                <button style={btnPrimary} onClick={refreshOnboarding} disabled={actionLoading}>
                  {actionLoading ? 'Gerando...' : '🔄 Completar Configuração'}
                </button>
              )}
              <button style={btnSecondary} onClick={openDashboard} disabled={actionLoading}>
                📊 Abrir Dashboard Stripe
              </button>
              <button style={btnSecondary} onClick={loadStatus} disabled={actionLoading}>
                🔄 Atualizar Status
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Saldo — só mostra se conectado e com cobranças ativas */}
      {status?.connected && status?.charges_enabled && (
        <div style={cardStyle}>
          <h3 style={{ fontSize: 16, fontWeight: 600, marginBottom: 16, color: 'var(--text, #fff)' }}>
            💰 Saldo
          </h3>
          {balance ? (
            <div style={{ display: 'flex', gap: 32 }}>
              <div>
                <div style={{ fontSize: 12, color: 'var(--text-secondary, #aaa)', marginBottom: 4 }}>Disponível</div>
                {balance.available?.map((b: any) => (
                  <div style={{ fontSize: 20, fontWeight: 700, color: '#22c55e' }}>
                    {formatMoney(b.amount, b.currency)}
                  </div>
                ))}
                {(!balance.available || balance.available.length === 0) && (
                  <div style={{ fontSize: 20, fontWeight: 700, color: 'var(--text-secondary, #aaa)' }}>$0,00</div>
                )}
              </div>
              <div>
                <div style={{ fontSize: 12, color: 'var(--text-secondary, #aaa)', marginBottom: 4 }}>Pendente</div>
                {balance.pending?.map((b: any) => (
                  <div style={{ fontSize: 20, fontWeight: 700, color: '#f59e0b' }}>
                    {formatMoney(b.amount, b.currency)}
                  </div>
                ))}
                {(!balance.pending || balance.pending.length === 0) && (
                  <div style={{ fontSize: 20, fontWeight: 700, color: 'var(--text-secondary, #aaa)' }}>$0,00</div>
                )}
              </div>
            </div>
          ) : (
            <p style={{ color: 'var(--text-secondary, #aaa)', fontSize: 14 }}>
              Nenhum saldo disponível ainda.
            </p>
          )}
        </div>
      )}

      {/* Info sobre o split */}
      <div style={{ ...cardStyle, background: 'var(--bg-card, #1a1a2e)', opacity: 0.8 }}>
        <h3 style={{ fontSize: 16, fontWeight: 600, marginBottom: 12, color: 'var(--text, #fff)' }}>
          ℹ️ Como funciona o split
        </h3>
        <div style={{ fontSize: 13, color: 'var(--text-secondary, #aaa)', lineHeight: 1.8 }}>
          <p>• Quando seus clientes pagam, o valor vai direto pra sua conta Stripe.</p>
          <p>• A taxa da plataforma ({status?.platform_fee_percent ?? 10}%) é descontada automaticamente.</p>
          <p>• Você gerencia reembolsos e disputas pelo seu dashboard Stripe.</p>
          <p>• Saques são configurados direto no Stripe (automáticos ou manuais).</p>
        </div>
      </div>
    </div>
  );
}

export default AdminStripeConnect;
